"use client";
import React from "react";
import { motion } from "framer-motion";
import { useThemeMode } from "../../contexts/ThemeContext";


export const TestimonialsColumn = (props: {
  className?: string;
  testimonials: typeof testimonials;
  duration?: number;
  isDark?: boolean;
}) => {
  return (
    <div className={props.className}>
      <motion.div
        animate={{
          y: "-50%",
        }}
        transition={{
          duration: props.duration || 10,
          repeat: Infinity,
          ease: "linear",
          repeatType: "loop",
        }}
        className="flex flex-col gap-6 pb-6"
        style={{
          backgroundColor: 'transparent'
        }}
      >
        {[
          ...new Array(2).fill(0).map((_, index) => (
            <React.Fragment key={index}>
              {props.testimonials.map(({ text, image, name, role }, i) => (
                <div 
                  className="p-10 rounded-3xl max-w-xs w-full transition-all duration-300" 
                  key={i}
                  style={{
                    background: props.isDark 
                      ? 'rgba(255, 255, 255, 0.05)' 
                      : 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: props.isDark
                      ? '1px solid rgba(255, 255, 255, 0.1)'
                      : '1px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: props.isDark
                      ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                      : '0 8px 32px rgba(31, 38, 135, 0.15)',
                    color: props.isDark ? '#ffffff' : '#000000'
                  }}
                >
                  <div>{text}</div>
                  <div className="flex items-center gap-2 mt-5">
                    <img
                      width={40}
                      height={40}
                      src={image}
                      alt={name}
                      className="h-10 w-10 rounded-full"
                    />
                    <div className="flex flex-col">
                      <div className="font-medium tracking-tight leading-5" style={{
                        color: props.isDark ? '#ffffff' : '#000000'
                      }}>{name}</div>
                      <div className="leading-5 tracking-tight" style={{
                        color: props.isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
                      }}>{role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </React.Fragment>
          )),
        ]}
      </motion.div>
    </div>
  );
};

const testimonials = [
  {
    name: "Sarah Chen",
    role: "Fitness Enthusiast",
    image: "/images/testimonials/sarah-chen.jpg",
    text: "Health-Tree has completely transformed my approach to wellness. The talent tree system makes building healthy habits feel like a game, and I've never been more motivated to stick to my goals.",
  },
  {
    name: "Michael Rodriguez",
    role: "Software Engineer", 
    image: "/images/testimonials/michael-rodriguez.jpg",
    text: "As a busy professional, I struggled to maintain consistent health habits. Health-Tree's weekly lock-in system and science-based recommendations have helped me extend my healthspan effectively.",
  },
  {
    name: "Dr. Emily Watson",
    role: "Medical Professional",
    image: "/images/testimonials/emily-watson.jpg", 
    text: "The analytics and progress tracking in Health-Tree are incredible. I can see exactly how my health investments are paying off over time, which keeps me motivated every day.",
  },
  {
    name: "James Park",
    role: "Entrepreneur",
    image: "/images/testimonials/james-park.jpg",
    text: "I love how Health-Tree cuts through all the longevity noise and focuses on what actually works. The 80/20 approach has saved me so much time while maximizing my health results.",
  },
  {
    name: "Lisa Thompson",
    role: "Yoga Instructor",
    image: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
    text: "The gamified approach to health tracking is brilliant. I find myself excited to unlock new talents and reach the next level in my wellness journey.",
  },
  {
    name: "David Kim",
    role: "Personal Trainer",
    image: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop", 
    text: "Health-Tree's science-backed protocols have helped me optimize my training routine. The data-driven insights are invaluable for making informed health decisions.",
  }
];

export default function TestimonialsColumns1() {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  
  return (
    <section className="pt-4 pb-0">
      <div className="container px-4 md:px-6">
        <div className="text-center mb-4">
          <h2 className="text-4xl font-bold tracking-tight mb-2" style={{
            color: isDark ? '#ffffff' : '#000000'
          }}>
            What Our Users Say
          </h2>
          <p className="text-lg max-w-3xl mx-auto" style={{
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
          }}>
            Join thousands who are actively expanding their health outcomes with our scientifically efficient health decision tree.
          </p>
        </div>
        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-hidden">
            <TestimonialsColumn
              testimonials={testimonials.slice(0, 2)}
              duration={15}
              isDark={isDark}
            />
            <TestimonialsColumn
              testimonials={testimonials.slice(2, 4)}
              duration={20}
              className="hidden md:block"
              isDark={isDark}
            />
            <TestimonialsColumn
              testimonials={testimonials.slice(4, 6)}
              duration={25}
              className="hidden lg:block"
              isDark={isDark}
            />
          </div>
          <div 
            className="pointer-events-none absolute inset-x-0 top-0 h-32"
            style={{
              background: isDark 
                ? 'linear-gradient(to bottom, #0f0f0f, transparent)'
                : 'linear-gradient(to bottom, #ffffff, transparent)'
            }}
          ></div>
          <div 
            className="pointer-events-none absolute inset-x-0 bottom-0 h-32"
            style={{
              background: isDark 
                ? 'linear-gradient(to top, #0f0f0f, transparent)'
                : 'linear-gradient(to top, #ffffff, transparent)'
            }}
          ></div>
        </div>
      </div>
    </section>
  );
}